import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Plus, ArrowLeft, ListBullets, SquaresFour, PencilSimple, CalendarBlank, CaretLeft, CaretRight, Lightning, FloppyDisk, ChartLine, FunnelSimple, X, Bookmark, Archive, Flag, CheckCircle, Trash, CurrencyDollar, ShieldCheck, ChatCircle, PaperPlaneTilt, MapPin, Note, Folder as FolderIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import AddressAutocomplete from "../components/AddressAutocomplete";
import GetDirectionsButton from "../components/GetDirectionsButton";
import BeforeAfterReportButton from "../components/BeforeAfterReportButton";
import ProjectGanttStrip from "../components/ProjectGanttStrip";
import NearbySuppliers from "../components/NearbySuppliers";
import PrivateNotes from "../components/PrivateNotes";
import NotesThread from "../components/notes/NotesThread";
import { ClientWaitingPill } from "../components/notes/ClientWaiting";
import { useClientWaiting } from "../hooks/useClientWaiting";
import EntityFiles from "../components/EntityFiles";
import { AiBriefButton } from "../components/AiFeatures";
import ClientPicker from "../components/ClientPicker";
import TaskOperativeChips from "../components/TaskOperativeChips";
import TaskAssigneePicker from "../components/TaskAssigneePicker";
import DateTimePicker from "../components/DateTimePicker";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { UserCircle, X as XIcon } from "@phosphor-icons/react";
import ClientVisibilityToggle from "../components/ClientVisibilityToggle";
import { RaiseInvoiceButton, InvoiceCompletionBanner, LinkedInvoicesCard, useLinkedInvoices, completionToast } from "../components/RaiseInvoicePanel";
import { fmtDate } from "../lib/dateUtil";

import DateInput from "../components/DateInput";
const TASK_STATUSES = ["not_started", "assigned", "in_progress", "awaiting_materials", "awaiting_review", "completed_by_vendor", "approved", "rejected", "cancelled"];
const PROJECT_STATUSES = ["draft", "planned", "in_progress", "on_hold", "awaiting_vendor", "awaiting_approval", "completed", "closed", "cancelled"];
const PRIORITIES = ["low", "medium", "high", "critical"];
const CATEGORIES = ["general", "electrical", "plumbing", "painting", "flooring", "roofing", "hvac", "landscaping", "structural", "cleaning", "security", "fire_safety"];
const RECURRENCE_PATTERNS = ["daily", "weekly", "monthly"];

/** Phase 250 — Inline-editable schedule cell.
 *
 * Renders a clickable button showing the task's start (and optional time)
 * + due dates. Clicking opens a popover with two calendar pickers and a
 * time input. Changes are PUT to /tasks/:id immediately and the parent's
 * `onChange` callback is invoked so the table can refresh.
 *
 * Operatives and suppliers already display `task.start_date` on their
 * portals (Phase 248), so any change here propagates to their schedule
 * automatically once they reload — no extra plumbing required.
 */
function TaskScheduleCell({ task, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [start, setStart] = useState(task?.start_date || "");
  const [startTime, setStartTime] = useState(task?.start_time || "");
  const [due, setDue] = useState(task?.due_date || "");

  // Re-sync if parent updates the task object
  useEffect(() => {
    setStart(task?.start_date || "");
    setStartTime(task?.start_time || "");
    setDue(task?.due_date || "");
  }, [task?.start_date, task?.start_time, task?.due_date, task?.id]);

  const fmt = (d) => {
    if (!d) return "";
    try {
      const dt = new Date(`${d}T00:00:00`);
      return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    } catch { return d; }
  };

  const persist = async (patch) => {
    if (disabled) return;
    setSaving(true);
    try {
      await API.put(`/tasks/${task.id}`, patch);
      onChange?.(task.id, patch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save schedule");
    } finally { setSaving(false); }
  };

  const handleStartDate = (d) => {
    setStart(d);
    persist({ start_date: d, start_time: d ? startTime : "" });
    if (!d) setStartTime("");
  };
  const handleStartTime = (t) => {
    setStartTime(t);
    if (start) persist({ start_time: t });
  };
  const handleDueDate = (d) => {
    setDue(d);
    persist({ due_date: d });
  };

  const empty = !start && !due;

  return (
    <Popover open={open} onOpenChange={(v) => { if (!disabled) setOpen(v); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`text-left rounded px-1.5 py-1 -my-1 hover:bg-muted/60 transition-colors min-w-[88px] ${disabled ? "cursor-default" : "cursor-pointer"}`}
          data-testid={`task-schedule-trigger-${task?.id || "row"}`}
          onClick={(e) => e.stopPropagation()}
        >
          {empty ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <CalendarBlank size={11} /> Schedule…
            </span>
          ) : (
            <div className="text-[11px] leading-tight">
              {start && (
                <p>
                  <span className="text-muted-foreground">Start: </span>
                  <span className="font-medium">{fmt(start)}{startTime ? ` · ${startTime}` : ""}</span>
                </p>
              )}
              {due && (
                <p>
                  <span className="text-muted-foreground">Due: </span>
                  <span className="font-medium">{fmt(due)}</span>
                </p>
              )}
            </div>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-3 space-y-3"
        onClick={(e) => e.stopPropagation()}
        data-testid={`task-schedule-popover-${task?.id || "row"}`}
      >
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Start date</label>
          <DateTimePicker
            date={start}
            time={startTime}
            onDateChange={handleStartDate}
            onTimeChange={handleStartTime}
            label="Pick a start date"
            data-testid={`task-schedule-start-${task?.id || "row"}`}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Due date</label>
          <DateTimePicker
            date={due}
            onDateChange={handleDueDate}
            label="Pick a due date"
            data-testid={`task-schedule-due-${task?.id || "row"}`}
          />
        </div>
        {saving && <p className="text-[10px] text-muted-foreground">Saving…</p>}
        {start && (
          <p className="text-[10px] text-muted-foreground border-t border-border pt-2">
            Pushed live to {task?.assigned_to_name || "the assignee"}'s schedule.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

function StatusBadge({ status }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium status-${status}`}>{status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>;
}
function PriorityBadge({ priority }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium priority-${priority}`}>{priority?.toUpperCase()}</span>;
}

const KANBAN_COLS = [
  { key: "not_started", label: "Not Started" },
  { key: "assigned", label: "Assigned" },
  { key: "in_progress", label: "In Progress" },
  { key: "completed_by_vendor", label: "Completed" },
  { key: "approved", label: "Approved" },
];

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const GANTT_STATUS_COLORS = {
  not_started: "#94a3b8", assigned: "#0ea5e9", in_progress: "#f59e0b",
  awaiting_materials: "#eab308", awaiting_review: "#8b5cf6", completed_by_vendor: "#14b8a6",
  approved: "#10b981", rejected: "#ef4444", cancelled: "#64748b"
};

function GanttChart({ tasks, project }) {
  const today = new Date();
  const projectStart = project.start_date ? new Date(project.start_date) : null;
  const projectEnd = project.target_date ? new Date(project.target_date) : null;

  // Determine timeline range
  const allDates = [];
  tasks.forEach(t => {
    if (t.created_at) allDates.push(new Date(t.created_at));
    if (t.due_date) allDates.push(new Date(t.due_date));
    if (t.started_at) allDates.push(new Date(t.started_at));
    if (t.completed_at) allDates.push(new Date(t.completed_at));
  });
  if (projectStart) allDates.push(projectStart);
  if (projectEnd) allDates.push(projectEnd);
  allDates.push(today);

  if (allDates.length < 2) {
    return <p className="text-center py-8 text-muted-foreground text-sm">Not enough date data for Gantt chart. Add due dates to tasks.</p>;
  }

  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
  // Add padding
  minDate.setDate(minDate.getDate() - 3);
  maxDate.setDate(maxDate.getDate() + 7);

  const totalDays = Math.max(1, Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)));

  const getPosition = (date) => {
    if (!date) return 0;
    const d = new Date(date);
    return Math.max(0, Math.min(100, ((d - minDate) / (maxDate - minDate)) * 100));
  };

  // Generate month markers
  const months = [];
  const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  while (cursor <= maxDate) {
    months.push({ date: new Date(cursor), pos: getPosition(cursor) });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const todayPos = getPosition(today);

  return (
    <div className="border border-border rounded-md overflow-hidden" data-testid="gantt-chart">
      {/* Timeline header */}
      <div className="relative h-8 bg-muted/50 border-b border-border">
        {months.map((m, i) => (
          <div key={i} className="absolute top-0 h-full flex items-center border-l border-border/50 px-1.5" style={{ left: `${m.pos}%` }}>
            <span className="text-[9px] text-muted-foreground font-medium whitespace-nowrap">
              {m.date.toLocaleString("default", { month: "short", year: "2-digit" })}
            </span>
          </div>
        ))}
        {/* Today marker */}
        <div className="absolute top-0 h-full w-px bg-primary z-10" style={{ left: `${todayPos}%` }}>
          <span className="absolute -top-0 left-1 text-[8px] text-primary font-bold">Today</span>
        </div>
      </div>

      {/* Tasks */}
      <div className="divide-y divide-border">
        {tasks.filter(t => !t.parent_task_id).map(t => {
          const startDate = t.started_at || t.created_at;
          const endDate = t.due_date || t.completed_at || new Date().toISOString();
          const barStart = getPosition(startDate);
          const barEnd = getPosition(endDate);
          const barWidth = Math.max(1, barEnd - barStart);
          const color = GANTT_STATUS_COLORS[t.status] || "#94a3b8";

          return (
            <div key={t.id} className="flex items-center h-10 hover:bg-muted/20 transition-colors group" data-testid={`gantt-row-${t.id}`}>
              <div className="w-48 shrink-0 px-3 flex items-center gap-2 border-r border-border overflow-hidden">
                <Link to={`tasks/${t.id}`} className="text-xs font-medium truncate hover:text-primary transition-colors" title={t.title}>
                  {t.title}
                </Link>
              </div>
              <div className="flex-1 relative h-full">
                {/* Today line */}
                <div className="absolute top-0 h-full w-px bg-primary/20 z-0" style={{ left: `${todayPos}%` }} />
                {/* Bar */}
                <div
                  className="absolute top-2 h-6 rounded-sm flex items-center px-1.5 text-white text-[9px] font-medium shadow-sm transition-all group-hover:shadow-md"
                  style={{ left: `${barStart}%`, width: `${barWidth}%`, minWidth: "20px", backgroundColor: color }}
                  title={`${t.title}: ${startDate?.split("T")[0] || "?"} - ${endDate?.split("T")[0] || "?"}`}
                >
                  <span className="truncate">{t.assigned_to_name || t.status?.replace(/_/g, " ")}</span>
                </div>
                {/* Dependencies */}
                {t.dependencies?.length > 0 && t.dependencies.map(depId => {
                  const depTask = tasks.find(dt => dt.id === depId);
                  if (!depTask) return null;
                  const depEnd = getPosition(depTask.due_date || depTask.completed_at || depTask.created_at);
                  return (
                    <div key={depId} className="absolute top-5 border-t border-dashed border-muted-foreground/40" style={{ left: `${depEnd}%`, width: `${Math.max(0, barStart - depEnd)}%` }} />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {tasks.filter(t => !t.parent_task_id).length === 0 && (
        <p className="text-center py-8 text-muted-foreground text-sm">No tasks to display on Gantt chart.</p>
      )}
    </div>
  );
}

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const { byId: waitingById } = useClientWaiting();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [taskForm, setTaskForm] = useState({ title: "", description: "", category: "general", priority: "medium", due_date: "", start_date: "", start_time: "", end_date: "", end_time: "", scheduled_dates: [], budget: "", estimated_cost: "", property_area: "", parent_task_id: "", dependencies: [], is_recurring: false, recurrence_pattern: "weekly", recurrence_interval: 1, recurrence_end_date: "", vendor_id: "", vendor_name: "", operative_id: "", operative_name: "" });
  const [editForm, setEditForm] = useState({});
  const [view, setView] = useState(searchParams.get("view") === "notes" ? "notes" : "list");
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [calMonth, setCalMonth] = useState(new Date());
  const [ganttData, setGanttData] = useState(null);
  // Task filters
  const [taskStatusFilter, setTaskStatusFilter] = useState("all");
  const [taskPriorityFilter, setTaskPriorityFilter] = useState("all");
  const [taskCategoryFilter, setTaskCategoryFilter] = useState("all");
  const [taskPresets, setTaskPresets] = useState([]);
  const [showSaveTaskPreset, setShowSaveTaskPreset] = useState(false);
  const [taskPresetName, setTaskPresetName] = useState("");
  const [activeTaskPresetId, setActiveTaskPresetId] = useState(null);
  const isAdmin = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const { invoices: linkedInvoices } = useLinkedInvoices("project", projectId, isAdmin);

  // Milestones state
  const [milestones, setMilestones] = useState([]);
  const [showCreateMilestone, setShowCreateMilestone] = useState(false);
  const [milestoneForm, setMilestoneForm] = useState({ title: "", description: "", target_date: "", color: "#0ea5e9" });
  const [editingMilestoneId, setEditingMilestoneId] = useState(null);

  // Costs state
  const [costData, setCostData] = useState(null);
  const [showAddCost, setShowAddCost] = useState(false);
  const [costForm, setCostForm] = useState({ category: "general", description: "", amount: "", vendor_name: "", invoice_ref: "", date: "" });

  // Supplier users for task assignment
  const [vendorUsers, setVendorUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [bulkSupplierId, setBulkSupplierId] = useState("");
  const [bulkSupplierName, setBulkSupplierName] = useState("");
  const [bulkAssigning, setBulkAssigning] = useState(false);

  // In-house team for project + task assignment
  const [inHouseTeam, setInHouseTeam] = useState([]);
  const [projectOperatives, setProjectOperatives] = useState([]);
  const [showAddProjectOperative, setShowAddProjectOperative] = useState(false);
  const [pickedProjectOperative, setPickedProjectOperative] = useState("");

  // Task Compliance state
  const [complianceDocs, setComplianceDocs] = useState([]);
  const [showUploadCompliance, setShowUploadCompliance] = useState(false);
  const [complianceForm, setComplianceForm] = useState({ doc_type: "rams", title: "", description: "", task_ids: [], vendor_id: "", vendor_name: "", expiry_date: "" });
  const [uploadingComplianceFile, setUploadingComplianceFile] = useState(false);
  const [complianceFileRef] = useState({ current: null });
  const [showRejectCompliance, setShowRejectCompliance] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");

  // Messages state
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const [assignedSuppliers, setAssignedSuppliers] = useState([]);
  const [showNearbySuppliers, setShowNearbySuppliers] = useState(false);
  const [messageChannel, setMessageChannel] = useState("all");
  const messagesEndRef = useRef(null);
  const messagesWsRef = useRef(null);
  const [showQuickMessage, setShowQuickMessage] = useState(false);
  const [quickMsgChannel, setQuickMsgChannel] = useState("all");
  const [quickMsgText, setQuickMsgText] = useState("");
  const [sendingQuickMsg, setSendingQuickMsg] = useState(false);

  const fetchProject = useCallback(async () => {
    try {
      const { data } = await API.get(`/projects/${projectId}`);
      setProject(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [projectId]);

  const fetchGanttData = useCallback(async () => {
    try {
      const { data } = await API.get(`/projects/${projectId}/gantt`);
      setGanttData(data);
    } catch (err) { console.error(err); }
  }, [projectId]);

  useEffect(() => { fetchProject(); }, [fetchProject]);
  useEffect(() => { if (view === "gantt") fetchGanttData(); }, [view, fetchGanttData]);

  const fetchComplianceDocs = useCallback(async () => {
    try {
      const { data } = await API.get(`/projects/${projectId}/task-compliance`);
      setComplianceDocs(data);
    } catch { setComplianceDocs([]); }
  }, [projectId]);

  useEffect(() => { if (view === "compliance") fetchComplianceDocs(); }, [view, fetchComplianceDocs]);

  // Fetch messages when messages view is active
  const fetchMessages = useCallback(async () => {
    setMessagesLoading(true);
    try {
      const params = messageChannel !== "all" ? `?limit=200&channel=${messageChannel}` : "?limit=200";
      const { data } = await API.get(`/projects/${projectId}/messages${params}`);
      setMessages(data.messages || []);
    } catch { setMessages([]); }
    finally { setMessagesLoading(false); }
  }, [projectId, messageChannel]);

  useEffect(() => { if (view === "messages") fetchMessages(); }, [view, fetchMessages]);

  // Fetch assigned suppliers on page load (for quick-message and messages view)
  useEffect(() => {
    API.get(`/projects/${projectId}/assigned-suppliers`).then(({ data }) => setAssignedSuppliers(data)).catch(() => {});
  }, [projectId]);

  // Fetch unread message count
  useEffect(() => {
    API.get(`/projects/${projectId}/messages/unread-count`).then(({ data }) => setUnreadMsgCount(data.count)).catch(() => {});
  }, [projectId]);

  // WebSocket for real-time messages
  useEffect(() => {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsHost = backendUrl.replace(/^https?:\/\//, "");
    const wsUrl = `${proto}//${wsHost}/api/ws/messages`;
    let reconnectTimer;
    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        messagesWsRef.current = ws;
        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === "project_message" && msg.data?.project_id === projectId) {
              setMessages(prev => [...prev, msg.data]);
              if (view === "messages") {
                API.get(`/projects/${projectId}/messages/unread-count`).then(({ data }) => setUnreadMsgCount(data.count)).catch(() => {});
              } else {
                setUnreadMsgCount(prev => prev + 1);
              }
            }
          } catch {}
        };
        ws.onclose = () => { reconnectTimer = setTimeout(connect, 5000); };
        ws.onerror = () => ws.close();
      } catch {}
    };
    connect();
    return () => { clearTimeout(reconnectTimer); messagesWsRef.current?.close(); };
  }, [projectId, view]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (view === "messages" && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, view]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;
    setSendingMessage(true);
    try {
      const payload = { text: messageText.trim(), channel: messageChannel };
      const { data } = await API.post(`/projects/${projectId}/messages`, payload);
      setMessages(prev => [...prev, data]);
      setMessageText("");
    } catch { toast.error("Failed to send message"); }
    finally { setSendingMessage(false); }
  };

  const handleSendQuickMessage = async () => {
    if (!quickMsgText.trim()) return;
    setSendingQuickMsg(true);
    try {
      const payload = { text: quickMsgText.trim(), channel: quickMsgChannel };
      await API.post(`/projects/${projectId}/messages`, payload);
      const recipientLabel = quickMsgChannel === "all"
        ? "all suppliers"
        : assignedSuppliers.find(s => s.id === quickMsgChannel)?.company_name || "supplier";
      toast.success(`Message sent to ${recipientLabel}`);
      setQuickMsgText("");
      setShowQuickMessage(false);
    } catch { toast.error("Failed to send message"); }
    finally { setSendingQuickMsg(false); }
  };

  useEffect(() => {
    API.get("/filter-presets", { params: { entity_type: "tasks" } })
      .then(({ data }) => setTaskPresets(data)).catch(() => {});
    API.get(`/projects/${projectId}/milestones`).then(({ data }) => setMilestones(data)).catch(() => {});
    if (["super_admin", "admin", "project_manager"].includes(user?.role)) {
      API.get(`/projects/${projectId}/costs`).then(({ data }) => setCostData(data)).catch(() => {});
      API.get("/users").then(({ data }) => setVendorUsers(data.filter(u => u.role === "vendor" && u.active))).catch(() => {});
      API.get("/suppliers?limit=500").then(({ data }) => {
        const list = Array.isArray(data) ? data : (data.vendors || []);
        setSuppliers(list);
      }).catch(() => {});
      API.get("/in-house/team").then(({ data }) => setInHouseTeam(data.team || [])).catch(() => {});
      API.get(`/projects/${projectId}/operatives`).then(({ data }) => setProjectOperatives(data.operatives || [])).catch(() => {});
    }
  }, [projectId]);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...taskForm,
        budget: parseFloat(taskForm.budget) || 0,
        estimated_cost: parseFloat(taskForm.estimated_cost) || 0,
      };
      // Strip frontend-only fields before submitting.
      delete payload.operative_id;
      delete payload.operative_name;
      if (!payload.is_recurring) {
        delete payload.recurrence_pattern;
        delete payload.recurrence_interval;
        delete payload.recurrence_end_date;
      }
      const { data } = await API.post(`/projects/${projectId}/tasks`, payload);
      // If admin picked an in-house operative (mutually exclusive with supplier), assign now.
      if (taskForm.operative_id && data?.id && !taskForm.vendor_id) {
        await API.put(`/tasks/${data.id}/assign`, {
          assigned_to: taskForm.operative_id,
          assigned_to_name: taskForm.operative_name,
          assigned_type: "in_house",
        });
      }
      if (taskForm.is_recurring && data?.id) {
        await API.put(`/tasks/${data.id}/set-recurring`, {
          is_recurring: true,
          recurrence_pattern: taskForm.recurrence_pattern,
          recurrence_interval: taskForm.recurrence_interval,
          recurrence_end_date: taskForm.recurrence_end_date
        });
      }
      toast.success("Task created");
      setShowCreateTask(false);
      setTaskForm({ title: "", description: "", category: "general", priority: "medium", due_date: "", start_date: "", start_time: "", end_date: "", end_time: "", scheduled_dates: [], budget: "", estimated_cost: "", property_area: "", parent_task_id: "", dependencies: [], is_recurring: false, recurrence_pattern: "weekly", recurrence_interval: 1, recurrence_end_date: "", vendor_id: "", vendor_name: "", operative_id: "", operative_name: "" });
      fetchProject();
    } catch { toast.error("Failed to create task"); }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await API.put(`/projects/${projectId}/status`, { status: newStatus });
      if (newStatus === "completed" && isAdmin && linkedInvoices.filter(i => i.status !== "cancelled").length === 0) completionToast(navigate, "project", projectId, "Project marked as completed");
      else toast.success("Project status updated");
      fetchProject();
    } catch { toast.error("Failed to update status"); }
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    try {
      await API.put(`/projects/${projectId}`, editForm);
      toast.success("Project updated");
      setShowEditProject(false);
      fetchProject();
    } catch { toast.error("Failed to update project"); }
  };

  const handleQuickStatusChange = async (taskId, newStatus) => {
    try {
      await API.put(`/tasks/${taskId}/status`, { status: newStatus });
      toast.success("Task status updated");
      fetchProject();
    } catch { toast.error("Failed to update"); }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const taskId = result.draggableId;
    const newStatus = result.destination.droppableId;
    if (result.source.droppableId === newStatus) return;
    handleQuickStatusChange(taskId, newStatus);
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]);
  };

  const handleBulkUpdate = async () => {
    if (!selectedTasks.length || !bulkStatus) return;
    try {
      await API.put("/bulk/tasks-status", { task_ids: selectedTasks, status: bulkStatus });
      toast.success(`Updated ${selectedTasks.length} tasks`);
      setSelectedTasks([]);
      setBulkStatus("");
      fetchProject();
    } catch { toast.error("Bulk update failed"); }
  };

  const handleBulkAssignSupplier = async () => {
    if (!selectedTasks.length || !bulkSupplierId) return;
    setBulkAssigning(true);
    try {
      await API.put("/bulk/tasks-assign-supplier", { task_ids: selectedTasks, vendor_id: bulkSupplierId, vendor_name: bulkSupplierName });
      toast.success(`Assigned ${selectedTasks.length} tasks to ${bulkSupplierName}`);
      // Offer to generate a Works Order
      const createWo = window.confirm(`Generate a Works Order (PO) for ${bulkSupplierName} with these ${selectedTasks.length} tasks?`);
      if (createWo) {
        try {
          const { data: woData } = await API.post("/works-orders/from-supplier-assignment", {
            project_id: projectId, supplier_id: bulkSupplierId, task_ids: selectedTasks,
          });
          if (woData.existing) {
            toast(`Draft WO ${woData.wo_number} already exists for this supplier`, { description: "Opening existing WO..." });
          } else {
            toast.success(`Works Order ${woData.wo_number} created with ${woData.tasks_count} tasks`);
          }
          navigate(`/work-orders`);
          return;
        } catch (err) { toast.error(err.response?.data?.detail || "Failed to create Works Order"); }
      }
      setSelectedTasks([]);
      setBulkSupplierId("");
      setBulkSupplierName("");
      fetchProject();
    } catch { toast.error("Bulk assign failed"); }
    finally { setBulkAssigning(false); }
  };

  const handleSaveAsTemplate = async (e) => {
    e.preventDefault();
    try {
      await API.post(`/project-templates/from-project/${projectId}`, {
        name: templateName,
        description: templateDesc
      });
      toast.success("Project saved as template");
      setShowSaveTemplate(false);
      setTemplateName("");
      setTemplateDesc("");
    } catch { toast.error("Failed to save as template"); }
  };

  const handleArchive = async () => {
    try {
      await API.put(`/projects/${projectId}/archive`);
      toast.success("Project archived");
      fetchProject();
    } catch { toast.error("Failed to archive"); }
  };

  const handleRestoreProject = async () => {
    try {
      await API.put(`/projects/${projectId}/restore`);
      toast.success("Project restored");
      fetchProject();
    } catch { toast.error("Failed to restore"); }
  };

  // Milestone handlers
  const handleCreateMilestone = async (e) => {
    e.preventDefault();
    try {
      if (editingMilestoneId) {
        await API.put(`/projects/${projectId}/milestones/${editingMilestoneId}`, milestoneForm);
        toast.success("Milestone updated");
      } else {
        await API.post(`/projects/${projectId}/milestones`, milestoneForm);
        toast.success("Milestone created");
      }
      setShowCreateMilestone(false);
      setEditingMilestoneId(null);
      setMilestoneForm({ title: "", description: "", target_date: "", color: "#0ea5e9" });
      const { data } = await API.get(`/projects/${projectId}/milestones`);
      setMilestones(data);
    } catch { toast.error("Failed to save milestone"); }
  };

  const handleUpdateMilestoneStatus = async (msId, status) => {
    try {
      const { data: res } = await API.put(`/projects/${projectId}/milestones/${msId}`, { status });
      if (res?.client_notified > 0) toast.success(`Milestone reached — ${res.client_notified} client portal user${res.client_notified > 1 ? "s" : ""} notified with today's photos`);
      else if (res?.client_notified === 0) toast.success("Milestone reached — client has no active portal users to notify");
      const { data } = await API.get(`/projects/${projectId}/milestones`);
      setMilestones(data);
    } catch { toast.error("Failed to update"); }
  };

  const handleDeleteMilestone = async (msId) => {
    try {
      await API.delete(`/projects/${projectId}/milestones/${msId}`);
      setMilestones(prev => prev.filter(m => m.id !== msId));
      toast.success("Milestone deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const calendarDays = useMemo(() => {
    const year = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [calMonth]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!project) return <div className="text-center py-12 text-muted-foreground">Project not found</div>;

  const tasks = project.tasks || [];
  const mainTasks = tasks.filter(t => !t.parent_task_id);
  const filteredMainTasks = mainTasks.filter(t => {
    if (taskStatusFilter !== "all" && t.status !== taskStatusFilter) return false;
    if (taskPriorityFilter !== "all" && t.priority !== taskPriorityFilter) return false;
    if (taskCategoryFilter !== "all" && t.category !== taskCategoryFilter) return false;
    return true;
  });
  const hasTaskFilters = taskStatusFilter !== "all" || taskPriorityFilter !== "all" || taskCategoryFilter !== "all";

  const applyTaskPreset = (preset) => {
    const f = preset.filters || {};
    setTaskStatusFilter(f.status || "all");
    setTaskPriorityFilter(f.priority || "all");
    setTaskCategoryFilter(f.category || "all");
    setActiveTaskPresetId(preset.id);
  };

  const clearTaskFilters = () => {
    setTaskStatusFilter("all");
    setTaskPriorityFilter("all");
    setTaskCategoryFilter("all");
    setActiveTaskPresetId(null);
  };

  const handleSaveTaskPreset = async (e) => {
    e.preventDefault();
    try {
      const filters = {};
      if (taskStatusFilter !== "all") filters.status = taskStatusFilter;
      if (taskPriorityFilter !== "all") filters.priority = taskPriorityFilter;
      if (taskCategoryFilter !== "all") filters.category = taskCategoryFilter;
      await API.post("/filter-presets", { name: taskPresetName, entity_type: "tasks", filters, color: "blue" });
      toast.success("Task filter preset saved");
      setShowSaveTaskPreset(false);
      setTaskPresetName("");
      const { data } = await API.get("/filter-presets", { params: { entity_type: "tasks" } });
      setTaskPresets(data);
    } catch { toast.error("Failed to save preset"); }
  };

  const handleDeleteTaskPreset = async (presetId) => {
    try {
      await API.delete(`/filter-presets/${presetId}`);
      if (activeTaskPresetId === presetId) setActiveTaskPresetId(null);
      const { data } = await API.get("/filter-presets", { params: { entity_type: "tasks" } });
      setTaskPresets(data);
    } catch {}
  };

  const addProjectOperative = async () => {
    if (!pickedProjectOperative) return;
    try {
      const { data } = await API.post(`/projects/${projectId}/assign-operative`, { operative_id: pickedProjectOperative });
      setProjectOperatives(data.operatives || []);
      setPickedProjectOperative("");
      setShowAddProjectOperative(false);
      toast.success("Operative added to project");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add operative");
    }
  };

  const removeProjectOperative = async (operativeId) => {
    try {
      await API.delete(`/projects/${projectId}/assign-operative/${operativeId}`);
      setProjectOperatives(prev => prev.filter(o => o.user_id !== operativeId));
      toast.success("Operative removed");
    } catch { toast.error("Failed to remove operative"); }
  };

  return (
    <div data-testid="project-detail-page">
      <div className="mb-6">
        <Link to="/projects" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"><ArrowLeft size={14} /> Back to Projects</Link>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight" data-testid="project-title">{project.name}</h1>
              <StatusBadge status={project.status} />
            </div>
            <p className="text-sm text-muted-foreground">{project.reference} | {project.property_address || "No address"}</p>
            {project.created_by_name && <p className="text-[10px] text-muted-foreground" data-testid="project-added-by">Added by {project.created_by_name}{project.created_at ? ` · ${new Date(project.created_at).toLocaleDateString("en-GB")}` : ""}</p>}
          </div>
          {isAdmin && (
            <div className="flex gap-2 shrink-0 flex-wrap">
              {(project.latitude || project.property_address) && (
                <GetDirectionsButton
                  address={project.property_address}
                  latitude={project.latitude}
                  longitude={project.longitude}
                  variant="default"
                  size="sm"
                  data-testid="project-directions-btn"
                />
              )}
              <Button variant="outline" size="sm" onClick={() => { setTemplateName(project.name + " Template"); setTemplateDesc(project.description || ""); setShowSaveTemplate(true); }} data-testid="save-as-template-btn">
                <FloppyDisk size={14} className="mr-1" /> Save as Template
              </Button>
              <BeforeAfterReportButton project={project} />
              <RaiseInvoiceButton sourceType="project" sourceId={projectId} testid="project-raise-invoice-btn" />
              <Button variant="outline" size="sm" onClick={() => { setEditForm(project); setShowEditProject(true); }} data-testid="edit-project-btn"><PencilSimple size={14} className="mr-1" /> Edit</Button>
              {project.status === "archived" ? (
                <Button variant="outline" size="sm" onClick={handleRestoreProject} data-testid="restore-project-btn">
                  <Archive size={14} className="mr-1" /> Restore
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={handleArchive} className="text-muted-foreground hover:text-destructive" data-testid="archive-project-btn">
                  <Archive size={14} className="mr-1" /> Archive
                </Button>
              )}
              <Select value={project.status} onValueChange={handleUpdateStatus}>
                <SelectTrigger className="w-40" data-testid="project-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {isAdmin && <InvoiceCompletionBanner sourceType="project" sourceId={projectId} reference={project.reference} completed={["completed", "closed"].includes(project.status)} invoices={linkedInvoices} />}

      {/* Project Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        {[
          ["Type", project.project_type?.replace(/_/g, " ")],
          ["Priority", null, <PriorityBadge key="p" priority={project.priority} />],
          ["Budget", `£${(project.budget || 0).toLocaleString()}`],
          ["Start", project.start_date || "-"],
          ["Target", project.target_date || "-"],
          ["Manager", project.manager_name || "-"],
        ].map(([label, value, el]) => (
          <div key={label} className="border border-border rounded-md p-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
            {el || <p className="text-sm font-medium capitalize">{value}</p>}
          </div>
        ))}
      </div>

      {/* Client info */}
      {(project.client_name || project.client_company || project.client_email || project.client_phone || project.client) && (
        <div className="border border-border rounded-md p-4 mb-6" data-testid="project-client-info">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Client</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Name</p>
              <p className="font-medium">{project.client_name || project.client || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Company</p>
              <p className="font-medium">{project.client_company || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Email</p>
              <p className="font-medium break-all">{project.client_email || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Phone</p>
              <p className="font-medium">{project.client_phone || "—"}</p>
            </div>
            {project.requester_contact_name && (
              <div className="col-span-2 md:col-span-4">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Requester</p>
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: project.requester_contact_color || "#3b82f6" }}
                  data-testid="project-requester-tag"
                >
                  {project.requester_contact_name}
                </span>
              </div>
            )}
          </div>
          {isAdmin && project.client_id && <ClientVisibilityToggle entityType="project" entityId={projectId} clientId={project.client_id} value={project.show_contractor_to_client} onChanged={fetchProject} />}
        </div>
      )}

      {isAdmin && <div className="mb-6"><LinkedInvoicesCard sourceType="project" sourceId={projectId} invoices={linkedInvoices} /></div>}

      {/* Progress */}
      <div className="border border-border rounded-md p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Progress</span>
          <span className="text-sm font-bold">{project.progress}%</span>
        </div>
        <Progress value={project.progress} className="h-2" />
        <p className="text-xs text-muted-foreground mt-2">{project.completed_count} of {project.task_count} tasks completed</p>
      </div>

      {/* In-House Team on this project */}
      {isAdmin && (
        <div className="border border-border rounded-md p-4 mb-6" data-testid="project-team-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <UserCircle size={16} className="text-amber-600" />
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">In-House Team</h3>
              <Badge variant="secondary" className="text-[10px]">{projectOperatives.length}</Badge>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowAddProjectOperative(true)} data-testid="add-project-operative-btn">
              <Plus size={14} className="mr-1" /> Add Operative
            </Button>
          </div>
          {projectOperatives.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No in-house operatives assigned yet. Add one so they can see this project in their portal.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="project-team-list">
              {projectOperatives.map(op => (
                <div key={op.user_id} className="inline-flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-full pl-2.5 pr-1 py-0.5 text-xs">
                  <UserCircle size={12} className="text-amber-600" />
                  <span className="font-medium" data-testid={`project-operative-${op.user_id}`}>{op.name}</span>
                  <button
                    onClick={() => removeProjectOperative(op.user_id)}
                    className="rounded-full hover:bg-red-100 dark:hover:bg-red-900/40 p-1 text-red-600 transition-colors"
                    title="Remove from project"
                    data-testid={`remove-operative-${op.user_id}`}
                  >
                    <XIcon size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Phase 251 — Mini Gantt timeline */}
      <ProjectGanttStrip
        tasks={tasks}
        onTaskClick={(t) => navigate(`/projects/${projectId}/tasks/${t.id}`)}
        onChange={() => fetchProject()}
      />

      {/* Tasks Section Header */}
      <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold font-[Chivo]">Tasks</h2>
          <Badge variant="secondary">{tasks.length}</Badge>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex border border-border rounded-md overflow-hidden">
            <button onClick={() => setView("list")} className={`px-3 py-1.5 text-xs ${view==="list" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="list-view-btn"><ListBullets size={14} /></button>
            <button onClick={() => setView("kanban")} className={`px-3 py-1.5 text-xs ${view==="kanban" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="kanban-view-btn"><SquaresFour size={14} /></button>
            <button onClick={() => setView("calendar")} className={`px-3 py-1.5 text-xs ${view==="calendar" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="calendar-view-btn"><CalendarBlank size={14} /></button>
            <button onClick={() => setView("gantt")} className={`px-3 py-1.5 text-xs ${view==="gantt" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="gantt-view-btn"><ChartLine size={14} /></button>
            <button onClick={() => setView("timeline")} className={`px-3 py-1.5 text-xs ${view==="timeline" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="timeline-view-btn"><Flag size={14} /></button>
            {isAdmin && <button onClick={() => setView("costs")} className={`px-3 py-1.5 text-xs ${view==="costs" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="costs-view-btn"><CurrencyDollar size={14} /></button>}
            <button onClick={() => setView("compliance")} className={`px-3 py-1.5 text-xs ${view==="compliance" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="compliance-view-btn"><ShieldCheck size={14} /></button>
            <button onClick={() => { setView("messages"); setUnreadMsgCount(0); }} className={`px-3 py-1.5 text-xs relative ${view==="messages" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="messages-view-btn">
              <ChatCircle size={14} />
              {unreadMsgCount > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-[16px] flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1">{unreadMsgCount}</span>}
            </button>
            {isAdmin && <button onClick={() => setView("notes")} title={waitingById[projectId] ? "Notes — client is waiting on a reply" : "Notes"} className={`px-3 py-1.5 text-xs relative ${view==="notes" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="notes-view-btn"><Note size={14} />{waitingById[projectId] && <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-background" data-testid="notes-view-waiting-dot" />}</button>}
            <button onClick={() => setView("files")} title="Files & Documents" className={`px-3 py-1.5 text-xs ${view==="files" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="files-view-btn"><FolderIcon size={14} /></button>
          </div>
          {isAdmin && <Button size="sm" onClick={() => setShowQuickMessage(true)} variant="outline" data-testid="quick-message-btn"><ChatCircle size={14} className="mr-1" /> Send Message</Button>}
          {isAdmin && (project.latitude || project.property_address || project.street_address || project.postcode) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowNearbySuppliers(s => !s)}
              data-testid="project-nearby-btn"
            >
              <MapPin size={14} className="mr-1" /> {showNearbySuppliers ? "Hide" : "Find"} Nearby Suppliers
            </Button>
          )}
          {isAdmin && <Button size="sm" onClick={() => setShowCreateTask(true)} data-testid="create-task-btn"><Plus size={14} className="mr-1" /> Add Task</Button>}
        </div>
      </div>

      {/* Nearby suppliers panel (collapsible) */}
      {isAdmin && showNearbySuppliers && (project.latitude || project.property_address || project.street_address || project.postcode) && (
        <div className="mb-4" data-testid="project-nearby-suppliers-panel">
          <NearbySuppliers
            projectId={projectId}
            radiusMiles={50}
            limit={8}
            title="Suppliers closest to this property"
            showTradeInput
            showAutoPick
            includeCompliance
            onPick={(s) => {
              if (!s.id) {
                toast.info(`${s.company_name} is not available — please invite them first.`);
                return;
              }
              setBulkSupplierId(s.id);
              setBulkSupplierName(s.company_name || s.contact_name || "");
              toast.success(`Ready to assign ${s.company_name}. Select tasks and click Assign.`);
            }}
            data-testid="project-nearby-suppliers"
          />
        </div>
      )}

      {/* Task Filter Presets */}
      {taskPresets.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="task-presets-bar">
          <Bookmark size={12} className="text-muted-foreground shrink-0" />
          {taskPresets.map(p => (
            <button key={p.id} onClick={() => applyTaskPreset(p)}
              className={`group inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${activeTaskPresetId === p.id ? "bg-primary/10 border-primary/30 text-primary" : "border-border bg-card hover:bg-muted text-muted-foreground hover:text-foreground"}`}
              data-testid={`task-preset-${p.id}`}>
              {p.name}
              <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); handleDeleteTaskPreset(p.id); }} className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 cursor-pointer" data-testid={`del-task-preset-${p.id}`}><X size={10} /></span>
            </button>
          ))}
        </div>
      )}

      {/* Task Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="task-filters-bar">
        <FunnelSimple size={14} className="text-muted-foreground shrink-0" />
        <Select value={taskStatusFilter} onValueChange={v => { setTaskStatusFilter(v); setActiveTaskPresetId(null); }}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="task-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={taskPriorityFilter} onValueChange={v => { setTaskPriorityFilter(v); setActiveTaskPresetId(null); }}>
          <SelectTrigger className="w-32 h-8 text-xs" data-testid="task-priority-filter"><SelectValue placeholder="All Priorities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {PRIORITIES.map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={taskCategoryFilter} onValueChange={v => { setTaskCategoryFilter(v); setActiveTaskPresetId(null); }}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="task-category-filter"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g," ").replace(/\b\w/g,ch=>ch.toUpperCase())}</SelectItem>)}
          </SelectContent>
        </Select>
        {hasTaskFilters && (
          <>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowSaveTaskPreset(true)} data-testid="save-task-preset-btn">
              <FloppyDisk size={12} className="mr-1" /> Save
            </Button>
            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={clearTaskFilters} data-testid="clear-task-filters-btn">
              <X size={12} className="mr-1" /> Clear
            </Button>
          </>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedTasks.length > 0 && isAdmin && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-primary/5 border border-primary/20 rounded-md animate-fade-in flex-wrap" data-testid="bulk-actions-bar">
          <Lightning size={16} className="text-primary" />
          <span className="text-sm font-medium">{selectedTasks.length} selected</span>
          <Select value={bulkStatus} onValueChange={setBulkStatus}>
            <SelectTrigger className="w-40 h-8" data-testid="bulk-status-select"><SelectValue placeholder="Set status..." /></SelectTrigger>
            <SelectContent>{TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" onClick={handleBulkUpdate} disabled={!bulkStatus} data-testid="bulk-update-btn">Apply Status</Button>
          <span className="text-muted-foreground text-xs">|</span>
          <Select value={bulkSupplierId || "pick"} onValueChange={v => {
            if (v === "pick") { setBulkSupplierId(""); setBulkSupplierName(""); return; }
            const s = suppliers.find(s => s.id === v);
            setBulkSupplierId(v);
            setBulkSupplierName(s?.company_name || s?.contact_name || "");
          }}>
            <SelectTrigger className="w-48 h-8" data-testid="bulk-supplier-select"><SelectValue placeholder="Assign Supplier..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pick">Select supplier...</SelectItem>
              {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name || s.contact_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={handleBulkAssignSupplier} disabled={!bulkSupplierId || bulkAssigning} data-testid="bulk-assign-btn">
            {bulkAssigning ? "Assigning..." : "Assign"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setSelectedTasks([]); setBulkSupplierId(""); setBulkSupplierName(""); }} data-testid="bulk-clear-btn">Clear</Button>
        </div>
      )}

      {/* LIST VIEW */}
      {view === "list" && (
        <div className="border border-border rounded-md overflow-hidden">
          {tasks.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground text-sm">No tasks yet. Add a task to get started.</p>
          ) : (
            <table className="w-full text-sm" data-testid="tasks-table">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  {isAdmin && <th className="w-10 px-3 py-2"><Checkbox checked={selectedTasks.length === filteredMainTasks.length && filteredMainTasks.length > 0} onCheckedChange={(c) => setSelectedTasks(c ? filteredMainTasks.map(t => t.id) : [])} data-testid="select-all-tasks" /></th>}
                  <th className="text-left px-4 py-2 font-semibold">Task</th>
                  <th className="text-left px-4 py-2 font-semibold hidden md:table-cell">Category</th>
                  <th className="text-left px-4 py-2 font-semibold">Priority</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                  <th className="text-left px-4 py-2 font-semibold hidden lg:table-cell">Assigned</th>
                  <th className="text-left px-4 py-2 font-semibold hidden lg:table-cell">Schedule</th>
                  {isAdmin && <th className="text-left px-4 py-2 font-semibold">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredMainTasks.map(t => {
                  const subtasks = tasks.filter(s => s.parent_task_id === t.id);
                  return [
                    <tr key={t.id} className="hover:bg-muted/30 transition-colors">
                      {isAdmin && <td className="px-3 py-2"><Checkbox checked={selectedTasks.includes(t.id)} onCheckedChange={() => toggleTaskSelection(t.id)} data-testid={`select-task-${t.id}`} /></td>}
                      <td className="px-4 py-2.5">
                        <Link to={`/projects/${projectId}/tasks/${t.id}`} className="font-medium hover:text-primary transition-colors" data-testid={`task-link-${t.id}`}>{t.title}</Link>
                        <p className="text-[10px] text-muted-foreground">
                          {t.reference}
                          {subtasks.length > 0 ? ` | ${subtasks.length} sub-tasks` : ""}
                          {t.dependencies?.length > 0 ? ` | ${t.dependencies.length} deps` : ""}
                          {t.is_recurring && <span className="ml-1 text-primary font-medium">| Recurring</span>}
                        </p>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell text-muted-foreground capitalize text-xs">{t.category?.replace(/_/g," ")}</td>
                      <td className="px-4 py-2.5"><PriorityBadge priority={t.priority} /></td>
                      <td className="px-4 py-2.5"><StatusBadge status={t.status} /></td>
                      <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-muted-foreground">
                        {isAdmin ? (
                          <div className="flex flex-col gap-1 items-start">
                            <TaskAssigneePicker task={t} compact onAssigned={() => fetchProject()} />
                            <TaskOperativeChips taskId={t.id} operatives={t.assigned_operatives || []} compact testIdPrefix={`task-row-ops-${t.id}`} />
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <span>{t.vendor_name || t.assigned_to_name || "-"}</span>
                            <TaskOperativeChips taskId={t.id} operatives={t.assigned_operatives || []} compact testIdPrefix={`task-row-ops-${t.id}`} />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-muted-foreground">
                        <TaskScheduleCell task={t} onChange={() => fetchProject()} disabled={!isAdmin} />
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-2.5">
                          <Select value={t.status} onValueChange={(v) => handleQuickStatusChange(t.id, v)}>
                            <SelectTrigger className="h-7 text-xs w-28" data-testid={`task-status-select-${t.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                      )}
                    </tr>,
                    ...subtasks.map(st => (
                      <tr key={st.id} className="hover:bg-muted/30 transition-colors bg-muted/20">
                        {isAdmin && <td className="px-3 py-1" />}
                        <td className="px-4 py-2 pl-10">
                          <Link to={`/projects/${projectId}/tasks/${st.id}`} className="text-sm hover:text-primary transition-colors">{st.title}</Link>
                          <p className="text-[9px] text-muted-foreground">{st.reference} (sub-task)</p>
                        </td>
                        <td className="px-4 py-2 hidden md:table-cell text-muted-foreground capitalize text-xs">{st.category?.replace(/_/g," ")}</td>
                        <td className="px-4 py-2"><PriorityBadge priority={st.priority} /></td>
                        <td className="px-4 py-2"><StatusBadge status={st.status} /></td>
                        <td className="px-4 py-2 hidden lg:table-cell text-xs text-muted-foreground">
                          {isAdmin ? (
                            <div className="flex flex-col gap-1 items-start">
                              <TaskAssigneePicker task={st} compact onAssigned={() => fetchProject()} />
                              <TaskOperativeChips taskId={st.id} operatives={st.assigned_operatives || []} compact testIdPrefix={`task-row-ops-${st.id}`} />
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <span>{st.vendor_name || st.assigned_to_name || "-"}</span>
                              <TaskOperativeChips taskId={st.id} operatives={st.assigned_operatives || []} compact testIdPrefix={`task-row-ops-${st.id}`} />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 hidden lg:table-cell text-xs text-muted-foreground">
                          <TaskScheduleCell task={st} onChange={() => fetchProject()} disabled={!isAdmin} />
                        </td>
                        {isAdmin && <td className="px-4 py-2" />}
                      </tr>
                    ))
                  ];
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* KANBAN VIEW with DnD */}
      {view === "kanban" && (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
            {KANBAN_COLS.map(col => {
              const colTasks = filteredMainTasks.filter(t => {
                if (col.key === "not_started") return t.status === "not_started" || (!KANBAN_COLS.find(c => c.key === t.status) && !["cancelled", "rejected"].includes(t.status));
                return t.status === col.key;
              });
              return (
                <Droppable key={col.key} droppableId={col.key}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}
                      className={`kanban-column min-w-[240px] flex-1 transition-colors ${snapshot.isDraggingOver ? "border-primary/50 bg-primary/5" : ""}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{col.label}</span>
                        <Badge variant="secondary" className="text-[10px]">{colTasks.length}</Badge>
                      </div>
                      <div className="space-y-2 min-h-[100px]">
                        {colTasks.map((t, index) => (
                          <Draggable key={t.id} draggableId={t.id} index={index} isDragDisabled={!isAdmin}>
                            {(provided, snapshot) => (
                              <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                className={`block border border-border rounded-md p-3 bg-card transition-shadow ${snapshot.isDragging ? "shadow-lg border-primary" : "hover:border-primary/40"}`}>
                                <Link to={`/projects/${projectId}/tasks/${t.id}`} className="block" data-testid={`kanban-card-${t.id}`}>
                                  <p className="text-sm font-medium mb-1 line-clamp-2">{t.title}</p>
                                  <div className="flex items-center justify-between">
                                    <PriorityBadge priority={t.priority} />
                                    <span className="text-[10px] text-muted-foreground">{fmtDate(t.due_date) || ""}</span>
                                  </div>
                                  {t.assigned_to_name && !isAdmin && <p className="text-[10px] text-muted-foreground mt-1">{t.assigned_to_name}</p>}
                                  {t.assigned_operatives?.length > 0 && (
                                    <div className="mt-1.5">
                                      <TaskOperativeChips taskId={t.id} operatives={t.assigned_operatives} compact testIdPrefix={`kanban-ops-${t.id}`} />
                                    </div>
                                  )}
                                </Link>
                                {isAdmin && (
                                  <div className="mt-2 -ml-1" onClick={(e) => e.stopPropagation()}>
                                    <TaskAssigneePicker task={t} compact onAssigned={() => fetchProject()} />
                                  </div>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>
      )}

      {/* CALENDAR VIEW */}
      {view === "calendar" && (
        <div className="border border-border rounded-md p-4" data-testid="calendar-view">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="icon" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1))} data-testid="cal-prev"><CaretLeft size={16} /></Button>
            <h3 className="text-sm font-semibold font-[Chivo]">{calMonth.toLocaleString("default", { month: "long", year: "numeric" })}</h3>
            <Button variant="ghost" size="icon" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1))} data-testid="cal-next"><CaretRight size={16} /></Button>
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {DAYS.map(d => <div key={d} className="text-center text-[10px] font-semibold text-muted-foreground uppercase py-2">{d}</div>)}
            {calendarDays.map((day, i) => {
              if (!day) return <div key={`e-${i}`} className="min-h-[80px]" />;
              const dateStr = `${calMonth.getFullYear()}-${String(calMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const dayTasks = tasks.filter(t => t.due_date === dateStr);
              const isToday = dateStr === new Date().toISOString().split("T")[0];
              return (
                <div key={day} className={`min-h-[80px] border border-border rounded p-1 ${isToday ? "bg-primary/5 border-primary/30" : ""}`}>
                  <span className={`text-xs font-medium ${isToday ? "text-primary" : "text-muted-foreground"}`}>{day}</span>
                  <div className="mt-1 space-y-0.5">
                    {dayTasks.slice(0, 3).map(t => (
                      <Link key={t.id} to={`/projects/${projectId}/tasks/${t.id}`}
                        className={`block text-[9px] px-1 py-0.5 rounded truncate status-${t.status} hover:opacity-80`}
                        data-testid={`cal-task-${t.id}`}>{t.title}</Link>
                    ))}
                    {dayTasks.length > 3 && <span className="text-[9px] text-muted-foreground pl-1">+{dayTasks.length - 3} more</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* GANTT VIEW */}
      {view === "gantt" && (
        <div data-testid="gantt-view">
          {ganttData ? (
            <GanttChart tasks={ganttData.tasks || []} project={ganttData.project || project} />
          ) : (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          )}
        </div>
      )}

      {/* TIMELINE / MILESTONES VIEW */}
      {view === "timeline" && (
        <div data-testid="timeline-view" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold font-[Chivo]">Project Milestones</h3>
            {isAdmin && (
              <Button size="sm" onClick={() => { setEditingMilestoneId(null); setMilestoneForm({ title: "", description: "", target_date: "", color: "#0ea5e9" }); setShowCreateMilestone(true); }} data-testid="add-milestone-btn">
                <Plus size={14} className="mr-1" /> Add Milestone
              </Button>
            )}
          </div>

          {/* Visual Timeline Bar */}
          {milestones.length > 0 && project.start_date && project.target_date && (
            <div className="border border-border rounded-md p-4" data-testid="milestone-timeline-bar">
              <div className="relative">
                {/* Timeline baseline */}
                <div className="h-1.5 bg-muted rounded-full relative">
                  {/* Progress fill */}
                  <div className="absolute inset-y-0 left-0 bg-primary/30 rounded-full" style={{ width: `${Math.min(100, Math.max(0, ((new Date() - new Date(project.start_date)) / (new Date(project.target_date) - new Date(project.start_date))) * 100))}%` }} />
                  {/* Today marker */}
                  {(() => {
                    const pctToday = ((new Date() - new Date(project.start_date)) / (new Date(project.target_date) - new Date(project.start_date))) * 100;
                    if (pctToday >= 0 && pctToday <= 100) {
                      return <div className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-foreground border-2 border-background z-10" style={{ left: `${pctToday}%` }} title="Today" />;
                    }
                    return null;
                  })()}
                  {/* Milestone markers */}
                  {milestones.map(ms => {
                    if (!ms.target_date) return null;
                    const pct = ((new Date(ms.target_date) - new Date(project.start_date)) / (new Date(project.target_date) - new Date(project.start_date))) * 100;
                    if (pct < -5 || pct > 105) return null;
                    const statusColors = { pending: ms.color || "#0ea5e9", in_progress: "#f59e0b", completed: "#10b981", missed: "#ef4444" };
                    return (
                      <div key={ms.id} className="absolute top-1/2 -translate-y-1/2 z-20 group" style={{ left: `${Math.min(100, Math.max(0, pct))}%` }}>
                        <div className="w-4 h-4 -ml-2 rounded-full border-2 border-background shadow-sm cursor-pointer" style={{ backgroundColor: statusColors[ms.status] || ms.color }} title={ms.title}>
                          {ms.status === "completed" && <CheckCircle size={10} className="text-white m-[1px]" />}
                        </div>
                        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden group-hover:block bg-popover border border-border rounded-md px-2 py-1 text-[10px] whitespace-nowrap shadow-md z-30">
                          <p className="font-semibold">{ms.title}</p>
                          <p className="text-muted-foreground">{ms.target_date}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Date labels */}
                <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
                  <span>{fmtDate(project.start_date)}</span>
                  <span>{project.target_date}</span>
                </div>
              </div>
            </div>
          )}

          {/* Milestones List */}
          {milestones.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-md">
              <Flag size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground mb-1">No milestones set</p>
              <p className="text-xs text-muted-foreground">Add milestones to track key dates and deliverables.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {milestones.map(ms => {
                const isPast = ms.target_date && new Date(ms.target_date) < new Date() && ms.status !== "completed";
                return (
                  <div key={ms.id} className={`border rounded-md p-4 transition-colors ${ms.status === "completed" ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50/30 dark:bg-emerald-900/5" : isPast ? "border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-900/5" : "border-border"}`} data-testid={`milestone-${ms.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: ms.color || "#0ea5e9" }} />
                        <div>
                          <h4 className="text-sm font-semibold">{ms.title}</h4>
                          {ms.description && <p className="text-xs text-muted-foreground mt-0.5">{ms.description}</p>}
                          <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                            <span>Target: <span className="font-medium text-foreground">{ms.target_date || "Not set"}</span></span>
                            {ms.completed_date && <span>Completed: <span className="font-medium text-emerald-600">{fmtDate(ms.completed_date)}</span></span>}
                            <span>By: {ms.created_by_name}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Select value={ms.status} onValueChange={v => handleUpdateMilestoneStatus(ms.id, v)}>
                          <SelectTrigger className="h-7 text-[10px] w-28" data-testid={`milestone-status-${ms.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["pending", "in_progress", "completed", "missed"].map(s => (
                              <SelectItem key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {isAdmin && (
                          <>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingMilestoneId(ms.id); setMilestoneForm({ title: ms.title, description: ms.description || "", target_date: ms.target_date || "", color: ms.color || "#0ea5e9" }); setShowCreateMilestone(true); }} data-testid={`edit-milestone-${ms.id}`}>
                              <PencilSimple size={14} />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteMilestone(ms.id)} data-testid={`delete-milestone-${ms.id}`}>
                              <Trash size={14} />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Milestone Create/Edit Dialog */}
          <Dialog open={showCreateMilestone} onOpenChange={setShowCreateMilestone}>
            <DialogContent className="max-w-md" data-testid="milestone-dialog">
              <DialogHeader>
                <DialogTitle className="font-[Chivo]">{editingMilestoneId ? "Edit Milestone" : "Add Milestone"}</DialogTitle>
                <DialogDescription>Track key dates and deliverables for this project.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateMilestone} className="space-y-4">
                <div><Label>Title *</Label><Input value={milestoneForm.title} onChange={e => setMilestoneForm({...milestoneForm, title: e.target.value})} required data-testid="milestone-title-input" /></div>
                <div><Label>Description</Label><Textarea value={milestoneForm.description} onChange={e => setMilestoneForm({...milestoneForm, description: e.target.value})} rows={2} data-testid="milestone-desc-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Target Date *</Label><DateInput value={milestoneForm.target_date} onChange={e => setMilestoneForm({...milestoneForm, target_date: e.target.value})} required data-testid="milestone-date-input" /></div>
                  <div><Label>Color</Label><Input type="color" value={milestoneForm.color} onChange={e => setMilestoneForm({...milestoneForm, color: e.target.value})} className="h-9" data-testid="milestone-color-input" /></div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowCreateMilestone(false)}>Cancel</Button>
                  <Button type="submit" data-testid="save-milestone-btn">{editingMilestoneId ? "Save Changes" : "Add Milestone"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* COSTS / BUDGET VIEW */}
      {view === "costs" && (
        <div data-testid="costs-view" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold font-[Chivo]">Cost & Budget Tracking</h3>
            {isAdmin && (
              <Button size="sm" onClick={() => { setCostForm({ category: "general", description: "", amount: "", vendor_name: "", invoice_ref: "", date: new Date().toISOString().split("T")[0] }); setShowAddCost(true); }} data-testid="add-cost-btn">
                <Plus size={14} className="mr-1" /> Add Expense
              </Button>
            )}
          </div>

          {/* Budget Summary Cards */}
          {costData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="cost-summary-cards">
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Budget</p>
                <p className="text-xl font-bold font-[Chivo]">£{(costData.budget || 0).toLocaleString()}</p>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Spent</p>
                <p className="text-xl font-bold font-[Chivo] text-amber-600 dark:text-amber-400">£{(costData.total_spent || 0).toLocaleString()}</p>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Remaining</p>
                <p className={`text-xl font-bold font-[Chivo] ${(costData.remaining || 0) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                  £{(costData.remaining || 0).toLocaleString()}
                </p>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Used</p>
                <p className={`text-xl font-bold font-[Chivo] ${(costData.variance_pct || 0) > 100 ? "text-red-600 dark:text-red-400" : (costData.variance_pct || 0) > 80 ? "text-amber-600 dark:text-amber-400" : ""}`}>
                  {costData.variance_pct || 0}%
                </p>
              </div>
            </div>
          )}

          {/* Budget Bar */}
          {costData && costData.budget > 0 && (
            <div className="border border-border rounded-md p-4" data-testid="budget-progress-bar">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Budget Utilization</span>
                <span className="text-xs font-semibold">{costData.variance_pct}% of £{(costData.budget || 0).toLocaleString()}</span>
              </div>
              <Progress value={Math.min(100, costData.variance_pct || 0)} className="h-2.5" />
            </div>
          )}

          {/* Cost Line Items Table */}
          {costData && costData.line_items && costData.line_items.length > 0 ? (
            <div className="border border-border rounded-md overflow-hidden" data-testid="cost-items-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-4 py-2.5 font-semibold">Description</th>
                    <th className="text-left px-4 py-2.5 font-semibold hidden md:table-cell">Category</th>
                    <th className="text-left px-4 py-2.5 font-semibold hidden lg:table-cell">Supplier</th>
                    <th className="text-left px-4 py-2.5 font-semibold hidden lg:table-cell">Invoice</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                    {isAdmin && <th className="text-center px-4 py-2.5 font-semibold w-16"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {costData.line_items.map(item => (
                    <tr key={item.id} className="hover:bg-muted/30 transition-colors" data-testid={`cost-item-${item.id}`}>
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-xs">{item.description || "-"}</p>
                        <p className="text-[10px] text-muted-foreground">{item.created_by_name}</p>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell text-xs capitalize text-muted-foreground">{item.category?.replace(/_/g, " ")}</td>
                      <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-muted-foreground">{item.vendor_name || "-"}</td>
                      <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-muted-foreground">{item.invoice_ref || "-"}</td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{item.date}</td>
                      <td className="px-4 py-2.5 text-right text-xs font-semibold">£{(item.amount || 0).toLocaleString()}</td>
                      {isAdmin && (
                        <td className="px-4 py-2.5 text-center">
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={async () => {
                            try {
                              await API.delete(`/projects/${projectId}/costs/${item.id}`);
                              const { data } = await API.get(`/projects/${projectId}/costs`);
                              setCostData(data);
                              toast.success("Cost item deleted");
                            } catch { toast.error("Failed to delete"); }
                          }} data-testid={`delete-cost-${item.id}`}>
                            <Trash size={12} />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 border border-dashed border-border rounded-md" data-testid="no-costs">
              <CurrencyDollar size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground mb-1">No expenses recorded</p>
              <p className="text-xs text-muted-foreground">Add expense line items to track project costs against the budget.</p>
            </div>
          )}

          {/* Add Cost Dialog */}
          <Dialog open={showAddCost} onOpenChange={setShowAddCost}>
            <DialogContent className="max-w-md" data-testid="add-cost-dialog">
              <DialogHeader>
                <DialogTitle className="font-[Chivo]">Add Expense</DialogTitle>
                <DialogDescription>Record a cost item for this project.</DialogDescription>
              </DialogHeader>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await API.post(`/projects/${projectId}/costs`, { ...costForm, amount: parseFloat(costForm.amount) || 0 });
                  toast.success("Expense added");
                  setShowAddCost(false);
                  const { data } = await API.get(`/projects/${projectId}/costs`);
                  setCostData(data);
                  fetchProject();
                } catch { toast.error("Failed to add expense"); }
              }} className="space-y-4">
                <div><Label>Description *</Label><Input value={costForm.description} onChange={e => setCostForm({...costForm, description: e.target.value})} required data-testid="cost-desc-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Amount *</Label><Input type="number" step="0.01" value={costForm.amount} onChange={e => setCostForm({...costForm, amount: e.target.value})} required data-testid="cost-amount-input" /></div>
                  <div><Label>Category</Label>
                    <Select value={costForm.category} onValueChange={v => setCostForm({...costForm, category: v})}>
                      <SelectTrigger data-testid="cost-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["general", "labor", "materials", "equipment", "subcontractor", "permits", "overhead", "contingency"].map(c => (
                          <SelectItem key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, ch => ch.toUpperCase())}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Supplier Name</Label><Input value={costForm.vendor_name} onChange={e => setCostForm({...costForm, vendor_name: e.target.value})} data-testid="cost-vendor-input" /></div>
                  <div><Label>Invoice Ref</Label><Input value={costForm.invoice_ref} onChange={e => setCostForm({...costForm, invoice_ref: e.target.value})} data-testid="cost-invoice-input" /></div>
                </div>
                <div><Label>Date</Label><DateInput value={costForm.date} onChange={e => setCostForm({...costForm, date: e.target.value})} data-testid="cost-date-input" /></div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowAddCost(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-cost-btn">Add Expense</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ============ COMPLIANCE VIEW ============ */}
      {view === "compliance" && (
        <div data-testid="compliance-view" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold font-[Chivo]">Task Compliance Documents</h3>
            <Button size="sm" onClick={() => {
              const isVendorUser = user?.role === "vendor";
              setComplianceForm({
                doc_type: "rams", title: "", description: "", task_ids: [],
                vendor_id: isVendorUser ? user._id : "", vendor_name: isVendorUser ? user.name : "", expiry_date: ""
              });
              setShowUploadCompliance(true);
            }} data-testid="upload-compliance-btn">
              <Plus size={14} className="mr-1" /> Upload Document
            </Button>
          </div>

          {/* Summary cards */}
          {complianceDocs.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3" data-testid="compliance-summary">
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Total</p>
                <p className="text-xl font-bold font-[Chivo]">{complianceDocs.length}</p>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Verified</p>
                <p className="text-xl font-bold font-[Chivo] text-emerald-600 dark:text-emerald-400">{complianceDocs.filter(d => d.status === "verified").length}</p>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Pending</p>
                <p className="text-xl font-bold font-[Chivo] text-amber-600 dark:text-amber-400">{complianceDocs.filter(d => d.status === "pending_review").length}</p>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Rejected</p>
                <p className="text-xl font-bold font-[Chivo] text-red-600 dark:text-red-400">{complianceDocs.filter(d => d.status === "rejected").length}</p>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Expiring Soon</p>
                <p className="text-xl font-bold font-[Chivo] text-orange-600 dark:text-orange-400">{complianceDocs.filter(d => d.expiry_status === "expiring_soon").length}</p>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Expired</p>
                <p className="text-xl font-bold font-[Chivo] text-red-700 dark:text-red-300">{complianceDocs.filter(d => d.expiry_status === "expired").length}</p>
              </div>
            </div>
          )}

          {/* Compliance docs table */}
          {complianceDocs.length > 0 ? (
            <div className="border border-border rounded-md overflow-hidden" data-testid="compliance-docs-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-4 py-2.5 font-semibold">Document</th>
                    <th className="text-left px-4 py-2.5 font-semibold hidden md:table-cell">Type</th>
                    <th className="text-left px-4 py-2.5 font-semibold hidden lg:table-cell">Supplier</th>
                    <th className="text-left px-4 py-2.5 font-semibold hidden lg:table-cell">Tasks</th>
                    <th className="text-left px-4 py-2.5 font-semibold hidden md:table-cell">File</th>
                    <th className="text-left px-4 py-2.5 font-semibold hidden md:table-cell">Expiry</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                    <th className="text-center px-4 py-2.5 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {complianceDocs.map(doc => {
                    const linkedTasks = (doc.task_ids || []).map(tid => {
                      const t = project?.tasks?.find(t2 => t2.id === tid);
                      return t ? t.title : tid.slice(0, 8);
                    });
                    return (
                      <tr key={doc.id} className="hover:bg-muted/30 transition-colors" data-testid={`compliance-doc-${doc.id}`}>
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-xs">{doc.title || doc.doc_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</p>
                          {doc.description && <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">{doc.description}</p>}
                          <p className="text-[10px] text-muted-foreground">{doc.created_by_name} | {fmtDate(doc.created_at)}</p>
                        </td>
                        <td className="px-4 py-2.5 hidden md:table-cell text-xs capitalize text-muted-foreground">{doc.doc_type?.replace(/_/g, " ")}</td>
                        <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-muted-foreground">{doc.vendor_name || "-"}</td>
                        <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-muted-foreground">
                          {linkedTasks.length > 0 ? linkedTasks.join(", ") : <span className="italic">All tasks</span>}
                        </td>
                        <td className="px-4 py-2.5 hidden md:table-cell text-xs">
                          {doc.file_name ? (
                            <span className="text-primary cursor-pointer hover:underline" onClick={async () => {
                              try {
                                const resp = await API.get(`/task-compliance/${doc.id}/download`, { responseType: "blob" });
                                const url = URL.createObjectURL(resp.data);
                                const a = document.createElement("a"); a.href = url; a.download = doc.file_name; a.click(); URL.revokeObjectURL(url);
                              } catch { toast.error("Download failed"); }
                            }} data-testid={`download-compliance-${doc.id}`}>{doc.file_name}</span>
                          ) : <span className="text-muted-foreground italic">No file</span>}
                        </td>
                        <td className="px-4 py-2.5 hidden md:table-cell text-xs" data-testid={`expiry-${doc.id}`}>
                          {doc.expiry_date ? (
                            <div>
                              <span className="text-muted-foreground">{fmtDate(doc.expiry_date)}</span>
                              {doc.expiry_status === "expired" && (
                                <span className="ml-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">EXPIRED</span>
                              )}
                              {doc.expiry_status === "expiring_soon" && (
                                <span className="ml-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 animate-pulse">EXPIRING</span>
                              )}
                              {doc.expiry_status === "valid" && (
                                <span className="ml-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">VALID</span>
                              )}
                            </div>
                          ) : <span className="text-muted-foreground italic">No expiry</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${
                            doc.status === "verified" ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400" :
                            doc.status === "rejected" ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400" :
                            "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                          }`} data-testid={`compliance-status-${doc.id}`}>
                            {doc.status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                          {doc.reviewed_by_name && <p className="text-[9px] text-muted-foreground mt-0.5">by {doc.reviewed_by_name}</p>}
                          {doc.review_notes && doc.status === "rejected" && <p className="text-[9px] text-red-500 mt-0.5">{doc.review_notes}</p>}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {isAdmin && doc.status === "pending_review" && (
                              <>
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-emerald-600" onClick={async () => {
                                  try {
                                    await API.put(`/task-compliance/${doc.id}/verify`, {});
                                    toast.success("Document verified"); fetchComplianceDocs();
                                  } catch { toast.error("Failed to verify"); }
                                }} title="Verify" data-testid={`verify-compliance-${doc.id}`}><CheckCircle size={14} /></Button>
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-red-600" onClick={() => { setShowRejectCompliance(doc); setRejectNotes(""); }} title="Reject" data-testid={`reject-compliance-${doc.id}`}><X size={14} /></Button>
                              </>
                            )}
                            {(isAdmin || doc.vendor_id === user?._id) && (
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={async () => {
                                try {
                                  await API.delete(`/task-compliance/${doc.id}`);
                                  toast.success("Document deleted"); fetchComplianceDocs();
                                } catch { toast.error("Failed to delete"); }
                              }} title="Delete" data-testid={`delete-compliance-${doc.id}`}><Trash size={12} /></Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 border border-dashed border-border rounded-md" data-testid="no-compliance-docs">
              <ShieldCheck size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground mb-1">No compliance documents uploaded</p>
              <p className="text-xs text-muted-foreground">Upload RAMS, risk assessments, method statements and other compliance docs for this project's tasks.</p>
            </div>
          )}

          {/* Upload Compliance Dialog */}
          <Dialog open={showUploadCompliance} onOpenChange={setShowUploadCompliance}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="upload-compliance-dialog">
              <DialogHeader>
                <DialogTitle className="font-[Chivo]">Upload Compliance Document</DialogTitle>
                <DialogDescription>Upload RAMS, risk assessments, or other compliance documentation linked to tasks on this project.</DialogDescription>
              </DialogHeader>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const payload = { ...complianceForm };
                  if (!payload.vendor_id && user?.role === "vendor") {
                    payload.vendor_id = user._id;
                    payload.vendor_name = user.name;
                  }
                  const { data: created } = await API.post(`/projects/${projectId}/task-compliance`, payload);
                  // Upload file if selected
                  const fileInput = document.getElementById("compliance-file-input");
                  if (fileInput?.files?.[0]) {
                    setUploadingComplianceFile(true);
                    const formData = new FormData();
                    formData.append("file", fileInput.files[0]);
                    await API.post(`/task-compliance/${created.id}/attach-file`, formData, {
                      headers: { "Content-Type": "multipart/form-data" }
                    });
                    setUploadingComplianceFile(false);
                  }
                  toast.success("Compliance document uploaded");
                  setShowUploadCompliance(false);
                  fetchComplianceDocs();
                } catch { setUploadingComplianceFile(false); toast.error("Failed to upload"); }
              }} className="space-y-4">
                <div>
                  <Label>Document Type *</Label>
                  <Select value={complianceForm.doc_type} onValueChange={v => setComplianceForm({...complianceForm, doc_type: v})}>
                    <SelectTrigger data-testid="compliance-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[
                        { v: "rams", l: "RAMS" }, { v: "risk_assessment", l: "Risk Assessment" },
                        { v: "method_statement", l: "Method Statement" }, { v: "safety_plan", l: "Safety Plan" },
                        { v: "permit_to_work", l: "Permit to Work" }, { v: "coshh_assessment", l: "COSHH Assessment" },
                        { v: "insurance_cert", l: "Insurance Certificate" }, { v: "training_cert", l: "Training Certificate" },
                        { v: "other", l: "Other" }
                      ].map(dt => <SelectItem key={dt.v} value={dt.v}>{dt.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Title *</Label><Input value={complianceForm.title} onChange={e => setComplianceForm({...complianceForm, title: e.target.value})} required placeholder="e.g. RAMS for Electrical Works" data-testid="compliance-title-input" /></div>
                <div><Label>Description</Label><Textarea value={complianceForm.description} onChange={e => setComplianceForm({...complianceForm, description: e.target.value})} rows={2} placeholder="Additional details..." /></div>
                <div><Label>Expiry Date</Label><DateInput value={complianceForm.expiry_date} onChange={e => setComplianceForm({...complianceForm, expiry_date: e.target.value})} data-testid="compliance-expiry-input" /><p className="text-[10px] text-muted-foreground mt-1">Auto-reminders sent at 30, 14, and 7 days before expiry</p></div>
                {/* Supplier selector (admin only) */}
                {isAdmin && (
                  <div>
                    <Label>Supplier</Label>
                    <Select value={complianceForm.vendor_id || "none"} onValueChange={v => {
                      if (v === "none") setComplianceForm({...complianceForm, vendor_id: "", vendor_name: ""});
                      else {
                        const s = suppliers.find(s => s.id === v);
                        setComplianceForm({...complianceForm, vendor_id: v, vendor_name: s?.company_name || s?.contact_name || ""});
                      }
                    }}>
                      <SelectTrigger data-testid="compliance-vendor-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No specific supplier</SelectItem>
                        {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name || s.contact_name}{s.trade ? ` (${s.trade})` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {/* Linked tasks multi-select */}
                <div>
                  <Label>Link to Tasks (optional)</Label>
                  <p className="text-[10px] text-muted-foreground mb-1.5">Select tasks this document applies to. Leave empty to apply to all project tasks.</p>
                  <div className="border border-border rounded-md max-h-40 overflow-y-auto p-2 space-y-1" data-testid="compliance-task-selector">
                    {(project?.tasks || []).filter(t => !t.parent_task_id).map(t => (
                      <label key={t.id} className="flex items-center gap-2 py-1 px-1.5 rounded hover:bg-muted/50 cursor-pointer text-xs">
                        <Checkbox
                          checked={complianceForm.task_ids.includes(t.id)}
                          onCheckedChange={(checked) => {
                            if (checked) setComplianceForm({...complianceForm, task_ids: [...complianceForm.task_ids, t.id]});
                            else setComplianceForm({...complianceForm, task_ids: complianceForm.task_ids.filter(id => id !== t.id)});
                          }}
                          data-testid={`compliance-task-check-${t.id}`}
                        />
                        <span className="flex-1">{t.title}</span>
                        {t.vendor_name && <span className="text-muted-foreground">{t.vendor_name}</span>}
                      </label>
                    ))}
                    {(!project?.tasks || project.tasks.filter(t => !t.parent_task_id).length === 0) && (
                      <p className="text-xs text-muted-foreground text-center py-2">No tasks in this project yet.</p>
                    )}
                  </div>
                </div>
                {/* File upload */}
                <div>
                  <Label>Attach File (PDF, DOC, images - max 15MB)</Label>
                  <Input id="compliance-file-input" type="file" className="mt-1" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" data-testid="compliance-file-input" />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowUploadCompliance(false)}>Cancel</Button>
                  <Button type="submit" disabled={uploadingComplianceFile} data-testid="submit-compliance-btn">
                    {uploadingComplianceFile ? "Uploading..." : "Upload Document"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Reject Compliance Dialog */}
          <Dialog open={!!showRejectCompliance} onOpenChange={(open) => { if (!open) setShowRejectCompliance(null); }}>
            <DialogContent className="max-w-sm" data-testid="reject-compliance-dialog">
              <DialogHeader>
                <DialogTitle className="font-[Chivo]">Reject Document</DialogTitle>
                <DialogDescription>Provide a reason for rejecting this compliance document.</DialogDescription>
              </DialogHeader>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await API.put(`/task-compliance/${showRejectCompliance.id}/reject`, { review_notes: rejectNotes });
                  toast.success("Document rejected");
                  setShowRejectCompliance(null);
                  fetchComplianceDocs();
                } catch { toast.error("Failed to reject"); }
              }} className="space-y-4">
                <div><Label>Reason *</Label><Textarea value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} rows={3} required placeholder="Why is this document being rejected?" data-testid="reject-reason-input" /></div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowRejectCompliance(null)}>Cancel</Button>
                  <Button type="submit" variant="destructive" data-testid="submit-reject-btn">Reject</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Create Task Dialog */}
      <Dialog open={showCreateTask} onOpenChange={setShowCreateTask}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-task-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Add Task</DialogTitle><DialogDescription>Create a new task for this project.</DialogDescription></DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Title *</Label>
              <AiBriefButton
                workType="task"
                category={taskForm.category}
                propertyAddress={project?.property_address}
                onApply={(brief) => setTaskForm(prev => ({
                  ...prev,
                  title: brief.title || prev.title,
                  description: brief.description || prev.description,
                  category: brief.suggested_category || prev.category,
                  priority: brief.priority_suggestion || prev.priority,
                }))}
              />
            </div>
            <Input value={taskForm.title} onChange={e => setTaskForm({...taskForm, title: e.target.value})} required data-testid="task-title-input" />
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={taskForm.category} onValueChange={v => setTaskForm({...taskForm, category: v})}>
                  <SelectTrigger data-testid="task-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g," ").replace(/\b\w/g,ch=>ch.toUpperCase())}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Priority</Label>
                <Select value={taskForm.priority} onValueChange={v => setTaskForm({...taskForm, priority: v})}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Start <span className="text-[10px] text-muted-foreground">(when work begins)</span></Label>
                <DateTimePicker
                  date={taskForm.start_date}
                  time={taskForm.start_time}
                  onDateChange={(d) => setTaskForm({...taskForm, start_date: d})}
                  onTimeChange={(t) => setTaskForm({...taskForm, start_time: t})}
                  label="Pick start"
                  data-testid="task-start-picker"
                />
              </div>
              <div>
                <Label>End <span className="text-[10px] text-muted-foreground">(optional)</span></Label>
                <DateTimePicker
                  date={taskForm.end_date}
                  time={taskForm.end_time}
                  onDateChange={(d) => setTaskForm({...taskForm, end_date: d})}
                  onTimeChange={(t) => setTaskForm({...taskForm, end_time: t})}
                  label="Pick end"
                  data-testid="task-end-picker"
                />
              </div>
            </div>
            {/* Phase 272 — Additional non-contiguous sessions (e.g. Mon + Wed) */}
            <div className="border-l-2 border-l-primary/30 pl-3 ml-1 space-y-2" data-testid="task-extra-sessions">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Additional sessions <span className="opacity-60">(optional — for tasks split across non-consecutive days)</span></Label>
                <Button type="button" variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => setTaskForm({...taskForm, scheduled_dates: [...(taskForm.scheduled_dates || []), { start_date: "", start_time: "", end_date: "", end_time: "" }]})} data-testid="task-add-session-btn">
                  <Plus size={12} className="mr-1" /> Add session
                </Button>
              </div>
              {(taskForm.scheduled_dates || []).map((s, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid={`task-session-${idx}`}>
                  <div>
                    <Label className="text-[10px]">Session {idx + 2} start</Label>
                    <DateTimePicker
                      date={s.start_date}
                      time={s.start_time}
                      onDateChange={(d) => { const arr = [...taskForm.scheduled_dates]; arr[idx] = { ...arr[idx], start_date: d }; setTaskForm({...taskForm, scheduled_dates: arr}); }}
                      onTimeChange={(t) => { const arr = [...taskForm.scheduled_dates]; arr[idx] = { ...arr[idx], start_time: t }; setTaskForm({...taskForm, scheduled_dates: arr}); }}
                      label="Start"
                      data-testid={`task-session-${idx}-start`}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px]">Session {idx + 2} end</Label>
                    <DateTimePicker
                      date={s.end_date}
                      time={s.end_time}
                      onDateChange={(d) => { const arr = [...taskForm.scheduled_dates]; arr[idx] = { ...arr[idx], end_date: d }; setTaskForm({...taskForm, scheduled_dates: arr}); }}
                      onTimeChange={(t) => { const arr = [...taskForm.scheduled_dates]; arr[idx] = { ...arr[idx], end_time: t }; setTaskForm({...taskForm, scheduled_dates: arr}); }}
                      label="End"
                      data-testid={`task-session-${idx}-end`}
                    />
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => setTaskForm({...taskForm, scheduled_dates: taskForm.scheduled_dates.filter((_, i) => i !== idx)})} data-testid={`task-session-${idx}-remove`}>
                    <Trash size={12} />
                  </Button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Due date <span className="text-[10px] text-muted-foreground">(deadline)</span></Label>
                <DateTimePicker
                  date={taskForm.due_date}
                  onDateChange={(d) => setTaskForm({...taskForm, due_date: d})}
                  label="Pick deadline"
                  data-testid="task-due-picker"
                />
              </div>
              <div><Label>Budget</Label><Input type="number" value={taskForm.budget} onChange={e => setTaskForm({...taskForm, budget: e.target.value})} placeholder="0.00" data-testid="task-budget-input" /></div>
            </div>
            {/* Supplier Assignment */}
            <div>
              <Label>Assign Supplier (optional)</Label>
              <Select value={taskForm.vendor_id || "none"} onValueChange={v => {
                if (v === "none") {
                  setTaskForm({...taskForm, vendor_id: "", vendor_name: ""});
                } else {
                  const s = suppliers.find(s => s.id === v);
                  setTaskForm({...taskForm, vendor_id: v, vendor_name: s?.company_name || s?.contact_name || "", operative_id: "", operative_name: ""});
                }
              }}>
                <SelectTrigger data-testid="task-vendor-select"><SelectValue placeholder="No supplier assigned" /></SelectTrigger>
                <SelectContent>
                  <div className="px-2 py-1.5 sticky top-0 bg-popover">
                    <input
                      type="text"
                      value={supplierSearch}
                      onChange={e => setSupplierSearch(e.target.value)}
                      placeholder="Search suppliers..."
                      className="w-full h-8 px-2 text-xs border border-border rounded-md bg-background outline-none focus:ring-1 focus:ring-primary"
                      data-testid="supplier-search-input"
                      onClick={e => e.stopPropagation()}
                    />
                  </div>
                  <SelectItem value="none">No supplier assigned</SelectItem>
                  {suppliers
                    .filter(s => !supplierSearch || (s.company_name || s.contact_name || "").toLowerCase().includes(supplierSearch.toLowerCase()) || (s.trade || "").toLowerCase().includes(supplierSearch.toLowerCase()))
                    .map(s => <SelectItem key={s.id} value={s.id}>{s.company_name || s.contact_name}{s.trade ? ` (${s.trade})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* In-House Operative Assignment (mutually exclusive with supplier) */}
            <div>
              <Label>Or Assign In-House Operative (optional)</Label>
              <Select value={taskForm.operative_id || "none"} onValueChange={v => {
                if (v === "none") {
                  setTaskForm({...taskForm, operative_id: "", operative_name: ""});
                } else {
                  const op = inHouseTeam.find(u => u._id === v);
                  setTaskForm({...taskForm, operative_id: v, operative_name: op?.name || op?.email || "", vendor_id: "", vendor_name: ""});
                }
              }}>
                <SelectTrigger data-testid="task-operative-select"><SelectValue placeholder={taskForm.vendor_id ? "Supplier selected — clear it to pick an operative" : "No operative assigned"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No operative assigned</SelectItem>
                  {inHouseTeam.map(u => (
                    <SelectItem key={u._id} value={u._id}>{u.name || u.email}</SelectItem>
                  ))}
                  {inHouseTeam.length === 0 && (
                    <div className="px-2 py-2 text-[11px] text-muted-foreground">
                      No in-house team yet. Add operatives under Settings → In-House Team.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            {/* Parent Task (Sub-task) */}
            <div>
              <Label>Parent Task (optional - makes this a sub-task)</Label>
              <Select value={taskForm.parent_task_id || "none"} onValueChange={v => setTaskForm({...taskForm, parent_task_id: v === "none" ? "" : v})}>
                <SelectTrigger data-testid="task-parent-select"><SelectValue placeholder="None (main task)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (main task)</SelectItem>
                  {mainTasks.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Dependencies */}
            <div>
              <Label>Dependencies (tasks that must complete first)</Label>
              <div className="border border-border rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
                {mainTasks.filter(t => t.id !== taskForm.parent_task_id).map(t => (
                  <label key={t.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-muted/50 px-2 py-1 rounded">
                    <Checkbox checked={taskForm.dependencies.includes(t.id)} onCheckedChange={(c) => {
                      setTaskForm(prev => ({...prev, dependencies: c ? [...prev.dependencies, t.id] : prev.dependencies.filter(id => id !== t.id)}));
                    }} />
                    <span>{t.title}</span>
                    <StatusBadge status={t.status} />
                  </label>
                ))}
                {mainTasks.length === 0 && <p className="text-xs text-muted-foreground">No tasks to depend on</p>}
              </div>
            </div>
            <div><Label>Property Area</Label><Input value={taskForm.property_area} onChange={e => setTaskForm({...taskForm, property_area: e.target.value})} placeholder="e.g. Unit 4, Roof" data-testid="task-area-input" /></div>
            <div><Label>Description</Label><Textarea value={taskForm.description} onChange={e => setTaskForm({...taskForm, description: e.target.value})} rows={3} data-testid="task-desc-input" /></div>

            {/* Recurring Task Options */}
            <div className="border border-border rounded-md p-3 space-y-3" data-testid="recurring-task-section">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Recurring Task</Label>
                  <p className="text-[10px] text-muted-foreground">Automatically recreate this task on a schedule</p>
                </div>
                <Switch
                  checked={taskForm.is_recurring}
                  onCheckedChange={(c) => setTaskForm({...taskForm, is_recurring: c})}
                  data-testid="recurring-toggle"
                />
              </div>
              {taskForm.is_recurring && (
                <div className="grid grid-cols-3 gap-3 animate-fade-in">
                  <div>
                    <Label className="text-xs">Pattern</Label>
                    <Select value={taskForm.recurrence_pattern} onValueChange={v => setTaskForm({...taskForm, recurrence_pattern: v})}>
                      <SelectTrigger className="h-8" data-testid="recurrence-pattern-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{RECURRENCE_PATTERNS.map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Interval</Label>
                    <Input type="number" min={1} max={52} value={taskForm.recurrence_interval} onChange={e => setTaskForm({...taskForm, recurrence_interval: parseInt(e.target.value) || 1})} className="h-8" data-testid="recurrence-interval-input" />
                  </div>
                  <div>
                    <Label className="text-xs">End Date</Label>
                    <DateInput value={taskForm.recurrence_end_date} onChange={e => setTaskForm({...taskForm, recurrence_end_date: e.target.value})} className="h-8" data-testid="recurrence-end-input" />
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateTask(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-task-btn">Create Task</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditProject} onOpenChange={setShowEditProject}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-project-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Edit Project</DialogTitle><DialogDescription>Update project details.</DialogDescription></DialogHeader>
          <form onSubmit={handleEditProject} className="space-y-4">
            <div><Label>Name</Label><Input value={editForm.name || ""} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="edit-project-name" /></div>
            <div>
              <Label>Property</Label>
              <AddressAutocomplete
                value={editForm.property_address || ""}
                onChange={v => setEditForm({...editForm, property_address: v})}
                onSelect={(a) => setEditForm(f => ({
                  ...f,
                  property_address: a.formatted_address || f.property_address,
                  street_address: a.street_address || "",
                  town_city: a.town_city || "",
                  county: a.county || "",
                  postcode: a.postcode || "",
                  latitude: a.latitude || null,
                  longitude: a.longitude || null,
                }))}
                data-testid="edit-project-address"
              />
            </div>
            <div><Label>Client</Label>
              <div className="mt-1">
                <ClientPicker value={editForm} onChange={(next) => setEditForm(next)} testIdPrefix="edit-project-client" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Budget</Label><Input type="number" value={editForm.budget || ""} onChange={e => setEditForm({...editForm, budget: parseFloat(e.target.value) || 0})} /></div>
              <div><Label>Target Date</Label><DateInput value={editForm.target_date || ""} onChange={e => setEditForm({...editForm, target_date: e.target.value})} /></div>
            </div>
            <div><Label>Description</Label><Textarea value={editForm.description || ""} onChange={e => setEditForm({...editForm, description: e.target.value})} rows={3} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditProject(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-project-btn">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={showAddProjectOperative} onOpenChange={setShowAddProjectOperative}>
        <DialogContent data-testid="add-project-operative-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Add In-House Operative</DialogTitle>
            <DialogDescription>Add a team member to this project so they can see it in their portal.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Operative</Label>
              <Select value={pickedProjectOperative} onValueChange={setPickedProjectOperative}>
                <SelectTrigger data-testid="pick-project-operative-select"><SelectValue placeholder="Select an operative…" /></SelectTrigger>
                <SelectContent>
                  {inHouseTeam
                    .filter(u => !projectOperatives.some(po => po.user_id === u._id))
                    .map(u => (
                      <SelectItem key={u._id} value={u._id}>{u.name || u.email}</SelectItem>
                    ))}
                  {inHouseTeam.length === 0 && (
                    <div className="px-2 py-2 text-[11px] text-muted-foreground">
                      No in-house team yet. Add operatives under Settings → In-House Team.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddProjectOperative(false); setPickedProjectOperative(""); }}>Cancel</Button>
            <Button onClick={addProjectOperative} disabled={!pickedProjectOperative} data-testid="confirm-add-project-operative-btn">Add to Project</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSaveTemplate} onOpenChange={setShowSaveTemplate}>
        <DialogContent data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Save as Template</DialogTitle>
            <DialogDescription>Save this project's structure as a reusable template. All main tasks will be included.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveAsTemplate} className="space-y-4">
            <div><Label>Template Name *</Label><Input value={templateName} onChange={e => setTemplateName(e.target.value)} required data-testid="template-name-input" /></div>
            <div><Label>Description</Label><Textarea value={templateDesc} onChange={e => setTemplateDesc(e.target.value)} rows={2} data-testid="template-desc-input" /></div>
            <p className="text-xs text-muted-foreground">{mainTasks.length} main tasks will be included in the template.</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSaveTemplate(false)}>Cancel</Button>
              <Button type="submit" data-testid="confirm-save-template-btn">Save Template</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Save Task Preset Dialog */}

      {/* ============ MESSAGES VIEW ============ */}
      {view === "notes" && isAdmin && (
        <div data-testid="notes-view" className="space-y-5">
          <div className="bg-card border border-border rounded-lg p-4" data-testid="project-notes-section">
            <NotesThread entityType="project" entityId={projectId} onChanged={fetchProject} folder="project-notes" />
          </div>
          <PrivateNotes entityType="project" entityId={projectId} />
        </div>
      )}

      {view === "files" && (
        <div data-testid="files-view" className="border border-border rounded-md p-4">
          <h3 className="text-base font-semibold mb-3">Files &amp; Documents</h3>
          <EntityFiles entityType="project" entityId={projectId} currentUser={user} />
        </div>
      )}


      {view === "messages" && (
        <div data-testid="messages-view" className="border border-border rounded-md flex flex-col" style={{ height: "500px" }}>
          <div className="px-4 py-3 border-b border-border flex items-center gap-2 flex-wrap">
            <ChatCircle size={16} className="text-primary" weight="duotone" />
            <h3 className="text-sm font-semibold font-[Chivo]">Project Messages</h3>
            <Badge variant="secondary" className="text-[10px]">{messages.length}</Badge>
            {assignedSuppliers.length > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground">Channel:</span>
                <select
                  value={messageChannel}
                  onChange={e => setMessageChannel(e.target.value)}
                  className="text-xs border border-border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid="message-channel-select"
                >
                  <option value="all">All Messages</option>
                  {assignedSuppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.company_name}{s.trade ? ` (${s.trade})` : ""}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Messages list */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="messages-list">
            {messagesLoading ? (
              <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>
            ) : messages.length === 0 ? (
              <div className="text-center py-12">
                <ChatCircle size={32} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No messages yet{messageChannel !== "all" ? ` with ${assignedSuppliers.find(s => s.id === messageChannel)?.company_name || "this supplier"}` : ""}</p>
                <p className="text-xs text-muted-foreground mt-1">Start the conversation by sending a message below.</p>
              </div>
            ) : (
              messages.map(msg => {
                const isOwn = msg.user_id === user?._id;
                return (
                  <div key={msg.id} className={`flex ${isOwn ? "justify-end" : "justify-start"}`} data-testid={`message-${msg.id}`}>
                    <div className={`max-w-[70%] ${isOwn ? "bg-primary text-primary-foreground" : "bg-muted"} rounded-lg px-3 py-2`}>
                      {!isOwn && (
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-[10px] font-semibold">{msg.user_name}</span>
                          <span className={`text-[9px] px-1.5 py-0 rounded-full ${msg.user_role === "vendor" ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"}`}>
                            {msg.user_role?.replace(/_/g, " ")}
                          </span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
                      <div className={`flex items-center gap-1.5 mt-1 ${isOwn ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                        <span className="text-[9px]">{new Date(msg.created_at).toLocaleString()}</span>
                        {msg.channel && msg.channel !== "all" && messageChannel === "all" && (
                          <span className={`text-[8px] px-1 py-0 rounded ${isOwn ? "bg-white/15" : "bg-background border border-border"}`}>
                            {assignedSuppliers.find(s => s.id === msg.channel)?.company_name || "Supplier"}
                          </span>
                        )}
                        {isOwn && msg.read_by && msg.read_by.length > 0 && (
                          <span className="text-[8px] flex items-center gap-0.5" data-testid={`read-receipt-${msg.id}`} title={`Read by: ${msg.read_by.map(r => r.name).join(", ")}`}>
                            <CheckCircle size={10} weight="fill" className="text-emerald-300" />
                            <span>Read{msg.read_by.length > 1 ? ` by ${msg.read_by.length}` : ` by ${msg.read_by[0].name}`}</span>
                          </span>
                        )}
                        {isOwn && (!msg.read_by || msg.read_by.length === 0) && (
                          <span className="text-[8px] flex items-center gap-0.5 opacity-50" data-testid={`sent-receipt-${msg.id}`}>
                            <CheckCircle size={10} weight="regular" />
                            <span>Sent</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message input */}
          <div className="border-t border-border px-4 py-3">
            <form onSubmit={handleSendMessage} className="flex items-center gap-2" data-testid="message-form">
              <Input
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                placeholder={messageChannel === "all" ? "Message all suppliers..." : `Message ${assignedSuppliers.find(s => s.id === messageChannel)?.company_name || "supplier"}...`}
                className="flex-1"
                disabled={sendingMessage}
                data-testid="message-input"
              />
              <Button type="submit" size="sm" disabled={sendingMessage || !messageText.trim()} data-testid="send-message-btn">
                <PaperPlaneTilt size={16} className={sendingMessage ? "animate-pulse" : ""} />
              </Button>
            </form>
          </div>
        </div>
      )}

      <Dialog open={showSaveTaskPreset} onOpenChange={setShowSaveTaskPreset}>
        <DialogContent data-testid="save-task-preset-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Save Task Filter Preset</DialogTitle><DialogDescription>Save your current task filters for quick access.</DialogDescription></DialogHeader>
          <form onSubmit={handleSaveTaskPreset} className="space-y-4">
            <div><Label>Preset Name *</Label><Input value={taskPresetName} onChange={e => setTaskPresetName(e.target.value)} required placeholder="e.g. Critical Overdue" data-testid="task-preset-name-input" /></div>
            <div className="border border-border rounded-md p-3 bg-muted/30">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Active Filters</p>
              <div className="flex flex-wrap gap-1.5">
                {taskStatusFilter !== "all" && <Badge variant="secondary" className="text-[10px]">Status: {taskStatusFilter.replace(/_/g, " ")}</Badge>}
                {taskPriorityFilter !== "all" && <Badge variant="secondary" className="text-[10px]">Priority: {taskPriorityFilter}</Badge>}
                {taskCategoryFilter !== "all" && <Badge variant="secondary" className="text-[10px]">Category: {taskCategoryFilter.replace(/_/g, " ")}</Badge>}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSaveTaskPreset(false)}>Cancel</Button>
              <Button type="submit" data-testid="confirm-save-task-preset-btn">Save Preset</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Message Dialog */}
      <Dialog open={showQuickMessage} onOpenChange={(open) => { if (!open) { setQuickMsgText(""); setQuickMsgChannel("all"); } setShowQuickMessage(open); }}>
        <DialogContent className="max-w-md" data-testid="quick-message-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Send Message</DialogTitle>
            <DialogDescription>Send a message to suppliers assigned to this project.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Recipient</Label>
              <Select value={quickMsgChannel} onValueChange={setQuickMsgChannel}>
                <SelectTrigger data-testid="quick-msg-recipient-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Message All Suppliers</SelectItem>
                  {assignedSuppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.company_name}{s.role ? ` (${s.role})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {assignedSuppliers.length === 0 && (
                <p className="text-[10px] text-muted-foreground mt-1">No suppliers assigned to tasks yet. Message will be visible to all.</p>
              )}
            </div>
            <div>
              <Label className="mb-1.5 block">Message</Label>
              <Textarea
                value={quickMsgText}
                onChange={e => setQuickMsgText(e.target.value)}
                rows={3}
                placeholder={quickMsgChannel === "all" ? "Message all suppliers..." : `Message ${assignedSuppliers.find(s => s.id === quickMsgChannel)?.company_name || "supplier"}...`}
                data-testid="quick-msg-text-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setQuickMsgText(""); setQuickMsgChannel("all"); setShowQuickMessage(false); }}>Cancel</Button>
            <Button onClick={handleSendQuickMessage} disabled={sendingQuickMsg || !quickMsgText.trim()} data-testid="quick-msg-send-btn">
              {sendingQuickMsg ? <span className="flex items-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> Sending...</span> : <span className="flex items-center gap-1"><PaperPlaneTilt size={14} /> Send</span>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
